import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog'

interface NotaItem {
  id: string | number
  tipo: string
  conteudo?: string | null
  signedUrl?: string | null
  legenda?: string | null
  legenda_audio_path?: string | null
  criado_em: string
  _uploading?: boolean
  _error?: string | null
  duracao_segundos?: number | null
}

interface CountChipProps {
  count: number
  label: string
}

interface SessaoCapturaResumoProps {
  open: boolean
  items: NotaItem[]
  seconds: number
  onConfirm: (titulo: string | null) => void
  onCancel: () => void
  onDescartar: () => void
}

function fmtDuracao(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

function CountChip({ count, label }: CountChipProps) {
  if (!count) return null
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-[22px] font-light text-[#f0f0f0]">{count}</span>
      <span className="text-[10px] text-[#555] uppercase tracking-wide">{label}</span>
    </div>
  )
}

export default function SessaoCapturaResumo({ open, items, seconds, onConfirm, onCancel, onDescartar }: SessaoCapturaResumoProps) {
  const [titulo, setTitulo] = useState<string>('')
  const [showDescartar, setShowDescartar] = useState<boolean>(false)

  const fotos    = items.filter(n => n.tipo === 'FOTO').length
  const audios   = items.filter(n => n.tipo === 'AUDIO').length
  const textos   = items.filter(n => n.tipo === 'TEXTO').length
  const desenhos = items.filter(n => n.tipo === 'DESENHO').length
  const total    = fotos + audios + textos + desenhos

  return (
    <>
      <AlertDialog open={open && !showDescartar}>
        <AlertDialogContent className="max-w-[360px] border-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Encerrar sessao?</AlertDialogTitle>
            <AlertDialogDescription className="text-[12px] text-muted">
              {total === 0 ? 'Nenhuma captura nesta sessao.' : `${total} captura${total > 1 ? 's' : ''} em ${fmtDuracao(seconds)}`}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {total > 0 && (
            <div className="flex items-center justify-around py-3 px-2 bg-white/[0.04] rounded-xl">
              <CountChip count={fotos} label="fotos" />
              <CountChip count={audios} label="audios" />
              <CountChip count={textos} label="textos" />
              <CountChip count={desenhos} label="croquis" />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-[0.09em] text-[#999] font-bold">
              Titulo (opcional)
            </label>
            <input
              value={titulo}
              onChange={e => setTitulo(e.target.value)}
              placeholder="Ex: Visita de obra #3"
              className="w-full px-3 py-2 rounded-lg bg-white/[0.06] border border-white/[0.10] text-[13px] text-[#f0f0f0] placeholder:text-[#444] outline-none focus:border-white/20 transition-colors"
            />
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <Button onClick={() => onConfirm(titulo || null)} className="w-full rounded-full">
              Encerrar e salvar
            </Button>
            <Button onClick={onCancel} variant="secondary" className="w-full rounded-full">
              Continuar capturando
            </Button>
            <button
              onClick={() => setShowDescartar(true)}
              className="w-full py-1.5 text-[11px] text-[#555] cursor-pointer border-none bg-transparent hover:text-[var(--color-vermelho)] transition-colors"
            >
              Descartar tudo
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmacao de descarte */}
      <AlertDialog open={showDescartar} onOpenChange={setShowDescartar}>
        <AlertDialogContent className="max-w-[360px] border-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar sessao?</AlertDialogTitle>
            <AlertDialogDescription>
              Todas as capturas serao apagadas permanentemente. Esta acao nao pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button
              onClick={() => { setShowDescartar(false); onDescartar() }}
              variant="destructive"
              className="w-full rounded-full"
            >
              Descartar tudo
            </Button>
            <AlertDialogCancel className="w-full rounded-full mt-0">
              Cancelar
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
